import React from 'react';
import { useApolloClient } from '@apollo/client';

import { EmailBulkSendDocument, type EmailBulkSendQuery } from '~/api/graphql';
import { type OutboxBulkSend, outboxRowBulkSendId } from './outbox-bulk-send-rows';
import type { OutboxRow } from './outbox-rows';

const NO_EMAIL_IDS: ReadonlyMap<string, string> = new Map();

/**
 * The bulk emails Outgoing's rows say they belong to that loadedBulkSends, the recent ones, left
 * out, as an older batch whose emails are still on screen, each read on its own so its emails can
 * sit under it. One whose read is in flight or failed is left out, and its emails stay where they
 * are.
 */
export const useMissingOutboxBulkSends = (
  rows: OutboxRow[],
  loadedBulkSends: OutboxBulkSend[],
  isSkipped: boolean
): OutboxBulkSend[] => {
  const client = useApolloClient();
  const missingIdsKey = React.useMemo(() => {
    if (isSkipped) return '';
    const loadedIds = new Set(loadedBulkSends.map(({ id }) => id));
    const missingIds = new Set(
      rows.flatMap((row) => {
        const bulkSendId = row.bulkSend ? undefined : outboxRowBulkSendId(row, NO_EMAIL_IDS);
        return bulkSendId && !loadedIds.has(bulkSendId) ? [bulkSendId] : [];
      })
    );
    return [...missingIds].sort().join(',');
  }, [rows, loadedBulkSends, isSkipped]);

  const [readBulkSends, setReadBulkSends] = React.useState<ReadonlyMap<string, OutboxBulkSend>>(
    () => new Map()
  );
  // Each is read once: a failed read leaves its emails ungrouped rather than asking again.
  const askedIds = React.useRef(new Set<string>());
  const isMounted = React.useRef(true);
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  React.useEffect(() => {
    const toRead = missingIdsKey.split(',').filter((id) => id && !askedIds.current.has(id));
    for (const id of toRead) {
      askedIds.current.add(id);
      client
        .query<EmailBulkSendQuery>({ query: EmailBulkSendDocument, variables: { id } })
        .then(({ data }) => {
          const bulkSend = data?.emailBulkSend;
          if (isMounted.current && bulkSend) {
            setReadBulkSends((read) => new Map(read).set(id, bulkSend));
          }
        })
        .catch(() => {});
    }
  }, [missingIdsKey, client]);

  return React.useMemo(
    () => missingIdsKey.split(',').flatMap((id) => readBulkSends.get(id) ?? []),
    [missingIdsKey, readBulkSends]
  );
};
